import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@dexma/ui-components';
import gtm from 'adapter/in/reports/config/gtm';
import i18n from 'asset/translation';
import useTextBlock from 'adapter/in/common/widget/TextBlock/useTextBlock';
import useBusinessInsight from 'adapter/in/common/widget/BusinessInsight/useBusinessInsight';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { CreateTextBlockCriteriaBuilder } from 'use-case/widgets/create/createTextBlockCriteria';
import { CreateBusinessInsightCriteriaBuilder } from 'use-case/widgets/create/createBusinessInsightCriteria';
import { useUseCaseAlerts } from 'adapter/in/common/hooks/useUseCaseAlerts';
import { dispatchReportLayoutsData } from 'adapter/in/reports/contexts/ReportLayoutDataContext';
import { dispatchTextBlockData } from 'adapter/in/reports/contexts/TextBlockContext';
import { isSome } from 'fp-ts/lib/Option';
import { ReadLayoutsCriteriaBuilder } from 'use-case/layout/read/readLayoutsCriteria';
import styled, { css } from 'styled-components';
import { ReportAnalysisCoordinates } from 'adapter/in/common/analysis/reportAnalysisCoordinates';
import { useNewAnalysisFromTemplateOverlayModal } from 'adapter/in/reports/components/AddWidgetDropdown/useNewAnalysisFromTemplateOverlayModal';
import { SaveReportTemplateLayoutUseCase } from 'adapter/in/reports/hooks/useSaveReportTemplateLayout';
import { useFeatures } from 'adapter/in/common/context/Features/data';
import { BusinessInsight } from 'domain/entity/feature';
import { AddWidgetModal } from 'adapter/in/reports/components/AddWidgetDropdown/AddWidgetModal';
import {
    ADD_WIDGET_MENU_KEY,
    getCatalogItemsForKey,
    useAddWidgetCatalog,
} from 'adapter/in/reports/components/AddWidgetDropdown/useAddWidgetCatalog';

type AddWidgetDropdownProps = {
    reportTemplateId: string;
    isLayoutOverlay?: boolean;
    contextPeriod?: string;
    onTextWidgetSelected?: () => void;
    onBusinessInsightWidgetSelected?: (businessInsight: BusinessInsight, businessInsightId: string) => void;
    analysisCoordinates?: ReportAnalysisCoordinates;
    updateTemplateLayout?: SaveReportTemplateLayoutUseCase;
};

export const AddWidgetDropdown = ({
    reportTemplateId,
    isLayoutOverlay,
    contextPeriod,
    onTextWidgetSelected,
    onBusinessInsightWidgetSelected,
    analysisCoordinates,
    updateTemplateLayout,
}: AddWidgetDropdownProps) => {
    const { createTextBlock } = useTextBlock();
    const { createBusinessInsight } = useBusinessInsight();
    const {
        execute: create,
        isLoading,
        data,
        error: createError,
    } = useCase(createTextBlock, false);
    const {
        execute: createBusinessInsightWidget,
        isLoading: isBusinessInsightLoading,
        data: businessInsightData,
        error: businessInsightCreateError,
    } = useCase(createBusinessInsight, false);
    const { readResult: features } = useFeatures();
    const { getLayouts } = dispatchReportLayoutsData();
    const [isCreating, setIsCreating] = useState(false);
    const [isCreatingBusinessInsight, setIsCreatingBusinessInsight] = useState(false);
    const { setEditorBeingEdited } = dispatchTextBlockData();
    const [pendingOverlayBusinessInsight, setPendingOverlayBusinessInsight] = useState<BusinessInsight | undefined>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const { categories, businessInsightsByMenuKey } = useAddWidgetCatalog(features);
    const [selectedCategoryKey, setSelectedCategoryKey] = useState(categories[0]?.key);
    const { handleOnConfirmModal, onConfirmModal } = useNewAnalysisFromTemplateOverlayModal(
        reportTemplateId,
        !!isLayoutOverlay,
        contextPeriod,
        analysisCoordinates,
        updateTemplateLayout
    );

    useUseCaseAlerts(data, createError, {
        successMessage: 'reports.reportTemplate.textBlockCreateSuccess',
        errorMessage: 'reports.reportTemplate.textBlockCreateError',
    });

    useUseCaseAlerts(businessInsightData, businessInsightCreateError, {
        successMessage: 'reports.reportTemplate.businessInsightCreateSuccess',
        errorMessage: 'reports.reportTemplate.businessInsightCreateError',
    });

    const isValidSelectionKey = categories.some(
        (category) => category.key === selectedCategoryKey || category.groups?.some((group) => group.key === selectedCategoryKey)
    );
    const resolvedSelectedCategoryKey = isValidSelectionKey ? selectedCategoryKey : categories[0]?.key ?? '';

    const handleSelectCategory = (key: string) => {
        setSelectedCategoryKey(key);
        setSearchTerm('');
    };

    const resetModalFilters = () => {
        setSearchTerm('');
        setSelectedCategoryKey(categories[0]?.key);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        resetModalFilters();
    };

    const filteredItems = useMemo(() => {
        const items = getCatalogItemsForKey(categories, resolvedSelectedCategoryKey);
        const normalizedSearchTerm = searchTerm.trim().toLowerCase();
        if (!normalizedSearchTerm) return items;

        return items.filter(
            (item) =>
                item.label.toLowerCase().includes(normalizedSearchTerm) ||
                item.description?.toLowerCase().includes(normalizedSearchTerm)
        );
    }, [categories, resolvedSelectedCategoryKey, searchTerm]);

    useEffect(() => {
        if (!isLoading && isCreating && reportTemplateId) {
            getLayouts(ReadLayoutsCriteriaBuilder.create().withDashboardId(reportTemplateId).build());
        }
    }, [isLoading, isCreating, reportTemplateId]);

    useEffect(() => {
        if (!isBusinessInsightLoading && isCreatingBusinessInsight && reportTemplateId) {
            getLayouts(ReadLayoutsCriteriaBuilder.create().withDashboardId(reportTemplateId).build());
        }
    }, [isBusinessInsightLoading, isCreatingBusinessInsight, reportTemplateId]);

    useEffect(() => {
        if (isSome(data)) {
            setEditorBeingEdited(data.value.id);
            setIsCreating(false);
        }
        if (isSome(createError)) {
            setIsCreating(false);
        }
    }, [data, createError]);

    useEffect(() => {
        if (isSome(businessInsightData) || isSome(businessInsightCreateError)) {
            setIsCreatingBusinessInsight(false);
        }
    }, [businessInsightData, businessInsightCreateError]);

    useEffect(() => {
        if (!pendingOverlayBusinessInsight || !isSome(businessInsightData)) return;

        onBusinessInsightWidgetSelected?.(pendingOverlayBusinessInsight, businessInsightData.value.id);
        setPendingOverlayBusinessInsight(undefined);
    }, [businessInsightData, onBusinessInsightWidgetSelected, pendingOverlayBusinessInsight]);

    useEffect(() => {
        if (pendingOverlayBusinessInsight && isSome(businessInsightCreateError)) {
            setPendingOverlayBusinessInsight(undefined);
        }
    }, [businessInsightCreateError, pendingOverlayBusinessInsight]);

    const handleMenuItemSelected = (key: string) => {
        closeModal();
        switch (key) {
            case ADD_WIDGET_MENU_KEY.ANALYSIS:
                handleOnConfirmModal();
                break;
            case ADD_WIDGET_MENU_KEY.RICH_TEXT:
                if (isLayoutOverlay) {
                    onTextWidgetSelected?.();
                    break;
                }
                create(CreateTextBlockCriteriaBuilder.create().withDashboardId(reportTemplateId).withRichText('').build());
                setIsCreating(true);
                break;
            default: {
                const selectedBusinessInsight = businessInsightsByMenuKey.get(key);
                if (!selectedBusinessInsight) break;

                const createBusinessInsightCriteria = CreateBusinessInsightCriteriaBuilder.create()
                    .withDashboardId(reportTemplateId)
                    .withType(selectedBusinessInsight.type)
                    .build();

                if (isLayoutOverlay) {
                    setPendingOverlayBusinessInsight(selectedBusinessInsight);
                    createBusinessInsightWidget(createBusinessInsightCriteria);
                    break;
                }
                createBusinessInsightWidget(createBusinessInsightCriteria);
                setIsCreatingBusinessInsight(true);
                break;
            }
        }
    };

    return (
        <StyledAddWidgetDropdown data-testid={isLayoutOverlay ? 'add-widget-overlay' : 'add-widget'} $isLayoutOverlay={isLayoutOverlay}>
            <Button
                kind='button'
                type='button'
                text={i18n.t('dashboards.edit.addWidget')}
                variant='primary'
                onClick={() => setIsModalOpen(true)}
                dataId={gtm.addWidget}
            />
            <AddWidgetModal
                open={isModalOpen}
                onClose={closeModal}
                categories={categories}
                selectedCategoryKey={resolvedSelectedCategoryKey}
                onSelectCategory={handleSelectCategory}
                searchTerm={searchTerm}
                onSearchChange={(event) => setSearchTerm(event.currentTarget.value)}
                items={filteredItems}
                onItemClick={handleMenuItemSelected}
            />
            {onConfirmModal}
        </StyledAddWidgetDropdown>
    );
};

const StyledAddWidgetDropdown = styled.div<{ $isLayoutOverlay?: boolean }>`
    display: inline-flex;

    ${({ $isLayoutOverlay }) =>
        $isLayoutOverlay &&
        css`
            > button {
                height: 1.5rem;
                padding: 0 0.75rem;
                font-size: 0.75rem;
                line-height: 1.5rem;
                white-space: nowrap;
            }
        `}
`;
